import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import axios from "@helpers/axios/private.axios";
import { USER, ALERT } from "@constants";
import { useTranslation } from "@helpers/translate";
import { upsertTradingViewTimezone } from "@helpers/timezone";
import { alpha3ToAlpha2 } from "@utils/countryCodeMap";

const getDateParts = (dateBirth) => {
  if (!dateBirth) {
    return {
      day: "",
      month: "",
      year: "",
    };
  }

  const dateObject = dayjs(dateBirth);

  if (!dateObject.isValid()) {
    return {
      day: "",
      month: "",
      year: "",
    };
  }

  return {
    day: dateObject.format("DD"),
    month: dateObject.format("MM"),
    year: dateObject.format("YYYY"),
  };
};

const normalizeSavedTextValue = (value) => {
  return typeof value === "string" ? value.trim() : "";
};

const normalizeSavedPhone = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.replace(/^\+/, "").trim();
};

export const useProfileEditing = (
  nickname,
  firstName,
  lastName,
  phone,
  dateBirth,
  country,
  timezone,
  countries,
) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();

  const nicknameInputRef = useRef(null);
  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const dayInputRef = useRef(null);
  const monthInputRef = useRef(null);
  const yearInputRef = useRef(null);

  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(null);

  const [editingValues, setEditingValues] = useState(() => ({
    nickname: nickname || "",
    first_name: firstName || "",
    last_name: lastName || "",
    country: country || "",
    phone: normalizeSavedPhone(phone),
    timezone: timezone || "",
    date_birth: getDateParts(dateBirth),
  }));

  useEffect(() => {
    if (editingField) {
      return;
    }

    setEditingValues({
      nickname: nickname || "",
      first_name: firstName || "",
      last_name: lastName || "",
      country: country || "",
      phone: normalizeSavedPhone(phone),
      timezone: timezone || "",
      date_birth: getDateParts(dateBirth),
    });
  }, [
    nickname,
    firstName,
    lastName,
    country,
    phone,
    timezone,
    dateBirth,
    editingField,
  ]);

  useEffect(() => {
    if (
      editingField === "date_birth" &&
      dayInputRef.current
    ) {
      dayInputRef.current.focus();
    }
  }, [editingField]);

  useEffect(() => {
    const inputRefs = {
      nickname: nicknameInputRef,
      first_name: firstNameInputRef,
      last_name: lastNameInputRef,
      phone: phoneInputRef,
    };

    const currentInput = inputRefs[editingField]?.current;

    if (!currentInput) {
      return;
    }

    currentInput.focus();

    if (
      typeof currentInput.setSelectionRange === "function"
    ) {
      const valueLength = currentInput.value.length;

      currentInput.setSelectionRange(
        valueLength,
        valueLength,
      );
    }
  }, [editingField]);

  const handleSaveField = useCallback(
    async (fieldName, value, options = {}) => {
      const { silent = false } = options;

      const normalizedValue =
        typeof value === "string" ? value.trim() : value;

      const savedFirstName =
        normalizeSavedTextValue(firstName);

      const savedLastName =
        normalizeSavedTextValue(lastName);

      const savedPhone = normalizeSavedPhone(phone);

      /*
       * Ім’я, прізвище та телефон можна встановити
       * лише один раз.
       */
      if (
        fieldName === "first_name" &&
        savedFirstName
      ) {
        return;
      }

      if (
        fieldName === "last_name" &&
        savedLastName
      ) {
        return;
      }

      if (fieldName === "phone" && savedPhone) {
        return;
      }

      /*
       * Не відправляємо порожні обов’язкові поля.
       */
      if (
        [
          "nickname",
          "first_name",
          "last_name",
          "phone",
        ].includes(fieldName) &&
        !normalizedValue
      ) {
        dispatch({
          type: ALERT.ALERT_ERROR,
          payload:
            __("profile.field_cannot_be_empty") ||
            "Field cannot be empty",
        });

        return;
      }

      setSavingField(fieldName);

      try {
        const payload = {};

        if (
          [
            "nickname",
            "first_name",
            "last_name",
          ].includes(fieldName)
        ) {
          payload[fieldName] = normalizedValue;
        } else if (fieldName === "phone") {
          const phoneValue = String(
            normalizedValue,
          ).replace(/^\+/, "");

          const normalizedPhone = `+${phoneValue}`;

          payload.phone_number = normalizedPhone;
          payload.phone = normalizedPhone;
        } else if (fieldName === "country") {
          const normalizeAlpha2 = (code) => {
            if (
              !code ||
              typeof code !== "string"
            ) {
              return null;
            }

            const trimmedCode = code.trim();

            if (trimmedCode.length !== 2) {
              return null;
            }

            return trimmedCode.toUpperCase();
          };

          let countryCode = value;

          if (
            typeof value === "object" &&
            value?.code
          ) {
            countryCode = value.code;
          }

          if (typeof countryCode === "string") {
            const alpha2 =
              normalizeAlpha2(countryCode);

            if (alpha2) {
              countryCode = alpha2;
            } else if (
              countryCode.trim().length === 3
            ) {
              const mappedCountry =
                alpha3ToAlpha2[
                  countryCode.trim().toUpperCase()
                ];

              const mappedAlpha2 =
                normalizeAlpha2(mappedCountry);

              if (mappedAlpha2) {
                countryCode = mappedAlpha2;
              }
            }
          }

          const countryItem = countries.find(
            (item) =>
              item.code === countryCode ||
              item.code_alpha2 === countryCode ||
              item.alpha2 === countryCode ||
              item.iso2 === countryCode,
          );

          if (countryItem) {
            const alpha2Code =
              countryItem.code_alpha2 ||
              countryItem.alpha2 ||
              countryItem.iso2 ||
              countryItem.code2 ||
              (countryItem.code?.length === 2
                ? countryItem.code
                : null);

            if (alpha2Code) {
              countryCode = alpha2Code;
            }
          } else if (
            typeof countryCode === "string" &&
            countryCode.length === 3
          ) {
            const originalAlpha3Code =
              countryCode.toUpperCase();

            const mappedCountry =
              alpha3ToAlpha2[originalAlpha3Code];

            const mappedAlpha2 =
              normalizeAlpha2(mappedCountry);

            if (mappedAlpha2) {
              countryCode = mappedAlpha2;
            } else {
              const countryByAlpha3 = countries.find(
                (item) =>
                  item.code === originalAlpha3Code ||
                  item.code_alpha3 ===
                    originalAlpha3Code ||
                  item.alpha3 === originalAlpha3Code ||
                  item.iso3 === originalAlpha3Code,
              );

              const alpha2FromAlpha3 =
                countryByAlpha3?.code_alpha2 ||
                countryByAlpha3?.alpha2 ||
                countryByAlpha3?.iso2 ||
                countryByAlpha3?.code2 ||
                (countryByAlpha3?.code?.length === 2
                  ? countryByAlpha3.code
                  : null);

              if (alpha2FromAlpha3) {
                countryCode = alpha2FromAlpha3;
              }
            }
          }

          if (
            typeof value === "string" &&
            !normalizeAlpha2(countryCode)
          ) {
            const countryByName = countries.find(
              (item) =>
                item.label_en === value ||
                item.label_ru === value,
            );

            if (countryByName) {
              countryCode =
                countryByName.code_alpha2 ||
                countryByName.alpha2 ||
                countryByName.iso2 ||
                countryByName.code2 ||
                countryByName.code;
            }
          }

          const normalizedCountry =
            typeof countryCode === "string"
              ? countryCode.trim().toUpperCase()
              : "";

          if (normalizedCountry.length !== 2) {
            dispatch({
              type: ALERT.ALERT_ERROR,
              payload:
                __("common.failed") ||
                "Invalid country",
            });

            return;
          }

          payload.country = normalizedCountry;
        } else if (fieldName === "timezone") {
          payload.timezone = value;
        }

        Object.keys(payload).forEach((key) => {
          if (
            payload[key] === "" ||
            payload[key] === null ||
            payload[key] === undefined
          ) {
            delete payload[key];
          }
        });

        if (Object.keys(payload).length === 0) {
          setEditingField(null);
          return;
        }

        const response = await axios.post(
          "/users/update",
          payload,
        );

        if (response.status !== 200) {
          return;
        }

        const responseUser =
          response?.data?.data?.user || {};

        if (
          [
            "nickname",
            "first_name",
            "last_name",
          ].includes(fieldName)
        ) {
          const savedValue =
            responseUser[fieldName] ??
            payload[fieldName];

          dispatch({
            type: USER.USER_FIELD,
            payload: {
              [fieldName]: savedValue,
            },
          });
        } else if (fieldName === "phone") {
          const phoneValue =
            responseUser.phone ??
            responseUser.phone_number ??
            payload.phone_number;

          dispatch({
            type: USER.USER_FIELD,
            payload: {
              phone: phoneValue,
              phone_number: phoneValue,
            },
          });
        } else if (fieldName === "country") {
          const savedCountry =
            responseUser.country ??
            payload.country;

          dispatch({
            type: USER.USER_FIELD,
            payload: {
              country: savedCountry,
            },
          });
        } else if (fieldName === "timezone") {
          const savedTimezone =
            responseUser.timezone ?? value;

          dispatch({
            type: USER.USER_FIELD,
            payload: {
              timezone: savedTimezone,
            },
          });

          upsertTradingViewTimezone(savedTimezone);
        }

        setEditingField(null);

        if (!silent) {
          dispatch({
            type: ALERT.ALERT_SUCCESS,
            payload:
              __("common.successful") ||
              "Updated successfully",
          });
        }
      } catch (error) {
        const errors =
          error.response?.data?.errors;

        if (errors) {
          Object.keys(errors).forEach((field) => {
            const errorMessages = errors[field];

            if (
              Array.isArray(errorMessages) &&
              errorMessages.length > 0
            ) {
              dispatch({
                type: ALERT.ALERT_ERROR,
                payload: errorMessages[0],
              });
            }
          });
        } else {
          const errorMessage =
            error.response?.data?.message ||
            __("common.failed") ||
            "Failed to update";

          dispatch({
            type: ALERT.ALERT_ERROR,
            payload: errorMessage,
          });
        }
      } finally {
        setSavingField(null);
      }
    },
    [
      countries,
      dispatch,
      __,
      firstName,
      lastName,
      phone,
    ],
  );

  const handleSaveDateOfBirth = async (
    dateParts,
  ) => {
    /*
     * Дату народження можна встановити лише один раз.
     * Перевірка знаходиться не тільки в компоненті,
     * але й безпосередньо перед запитом.
     */
    const existingDate = dateBirth
      ? dayjs(dateBirth)
      : null;

    if (existingDate?.isValid()) {
      return;
    }

    const {
      day = "",
      month = "",
      year = "",
    } = dateParts || {};

    if (!day || !month || !year) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_empty") ||
          "Please fill in all date fields",
      });

      return;
    }

    const dayNumber = parseInt(day, 10);
    const monthNumber = parseInt(month, 10);

    if (dayNumber < 1 || dayNumber > 31) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_day") ||
          "Day must be between 1 and 31",
      });

      return;
    }

    if (monthNumber < 1 || monthNumber > 12) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_month") ||
          "Month must be between 1 and 12",
      });

      return;
    }

    if (year.length !== 4) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_year_format") ||
          "Year must be 4 digits",
      });

      return;
    }

    const dateStringForValidation =
      `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

    const dateObject = dayjs(
      dateStringForValidation,
    );

    const isExactDate =
      dateObject.isValid() &&
      dateObject.format("YYYY-MM-DD") ===
        dateStringForValidation;

    if (!isExactDate) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_invalid") ||
          "Invalid date",
      });

      return;
    }

    if (dateObject.isAfter(dayjs())) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_future") ||
          "Date of birth cannot be in the future",
      });

      return;
    }

    const age = dayjs().diff(dateObject, "year");

    if (age < 18) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_age_min") ||
          "You must be at least 18 years old",
      });

      return;
    }

    if (age > 120) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("profile.date_error_age_max") ||
          "Please enter a valid date of birth",
      });

      return;
    }

    const dateString =
      dateObject.format("YYYY-MM-DD");

    setSavingField("date_birth");

    try {
      const response = await axios.post(
        "/users/update",
        {
          date_birth: dateString,
        },
      );

      if (response.status !== 200) {
        return;
      }

      const savedDateBirth =
        response?.data?.data?.user?.date_birth ??
        dateString;

      dispatch({
        type: USER.USER_FIELD,
        payload: {
          date_birth: savedDateBirth,
        },
      });

      setEditingField(null);

      dispatch({
        type: ALERT.ALERT_SUCCESS,
        payload:
          __("common.successful") ||
          "Updated successfully",
      });
    } catch (error) {
      const errors =
        error.response?.data?.errors;

      if (errors?.date_birth?.length) {
        dispatch({
          type: ALERT.ALERT_ERROR,
          payload: errors.date_birth[0],
        });

        return;
      }

      const errorMessage =
        error.response?.data?.message ||
        __("common.failed") ||
        "Failed to update";

      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: errorMessage,
      });
    } finally {
      setSavingField(null);
    }
  };

  return {
    editingField,
    setEditingField,
    editingValues,
    setEditingValues,
    savingField,
    nicknameInputRef,
    firstNameInputRef,
    lastNameInputRef,
    phoneInputRef,
    dayInputRef,
    monthInputRef,
    yearInputRef,
    handleSaveField,
    handleSaveDateOfBirth,
  };
};
